import { Box, Button, Typography } from "@bca/ui-library";
import { useEffect, useState } from "react";

import {
  DEFAULT_PURCHASES_PARAMETERS,
  DEFAULT_SORT_OPTION_LABEL,
} from "../../pages";
import { PurchasesUrlQueryParams } from "../APICalls/buildGetPurchasesUrl";
import { AppliedCheckboxResult } from "../Hooks/useAppliedCheckBoxes";
import FilterSolidSVG from "../Icons/FilterSolidSVG";
import ActiveFilters from "./ActiveFilters";
import FiltersMobileLoader from "./FiltersMobileLoader";

const DISTANCE_FROM_TOP_TO_MOBILE_FILTER = 269;

interface FiltersMobileProps {
  showDateRangeMobile: () => void;
  isLoading?: boolean;
  selectedFilterLabel: string;
  selectedSortOptionLabel?: string;
  purchasesUrlQueryParams: PurchasesUrlQueryParams;
  setPurchasesUrlParams: (purchasesUrlParams: PurchasesUrlQueryParams) => void;
  isClearAllDisabled?: boolean;
  onClearAll: () => void;
  updateAdditionalFilters?;
}

const FiltersMobile: React.FunctionComponent<
  FiltersMobileProps & AppliedCheckboxResult
> = ({
  showDateRangeMobile,
  selectedFilterLabel,
  selectedSortOptionLabel = DEFAULT_SORT_OPTION_LABEL,
  isLoading = false,
  purchasesUrlQueryParams,
  setPurchasesUrlParams,
  appliedCheckboxes,
  setAppliedCheckboxes,
  isClearAllDisabled,
  onClearAll,
  updateAdditionalFilters,
  setAppliedCheckboxesSingleSelect,
}) => {
  const [isScrolledToTop, setIsScrolledToTop] = useState<boolean>(true);

  const scrollToTop = () => {
    const onScroll = (event) => {
      setIsScrolledToTop(
        event.target.documentElement.scrollTop <
          DISTANCE_FROM_TOP_TO_MOBILE_FILTER
      );
    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  };

  useEffect(scrollToTop, [setIsScrolledToTop]);

  let activeFilters = 0;
  if (purchasesUrlQueryParams.sort !== DEFAULT_PURCHASES_PARAMETERS.sort) {
    activeFilters += 1;
  }
  if (purchasesUrlQueryParams.q !== DEFAULT_PURCHASES_PARAMETERS.q) {
    activeFilters += 1;
  }
  if (
    purchasesUrlQueryParams.numberofDays !==
    DEFAULT_PURCHASES_PARAMETERS.numberofDays
  ) {
    activeFilters += 1;
  }

  // after adding payments single select option we need to check this is still working properly
  if (
    purchasesUrlQueryParams.additionalFilters.bq !==
    DEFAULT_PURCHASES_PARAMETERS.additionalFilters.bq
  ) {
    activeFilters += 1;
  }

  if (isLoading) return <FiltersMobileLoader />;

  return (
    <Box
      backgroundColor="primary-white"
      display="flex"
      flexDirection="column"
      css={`
        position: sticky;
        top: 0;
        z-index: 1;
        box-shadow: ${!isScrolledToTop
          ? "0px 3px 5px rgba(218, 218, 218, 0.5);"
          : "none"};
      `}
      mx={-5}
      px={5}
    >
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Button
          fontSize="body-small"
          disabled={isLoading}
          marginTop={5}
          width="120px"
          height="auto"
          paddingX={4}
          paddingY={3}
          marginBottom={20}
          variant="button"
          onClick={showDateRangeMobile}
          px="10px"
        >
          <FilterSolidSVG width="19px" height="14px"></FilterSolidSVG>
          <Typography marginY="0" color="black" variant="body-small">
            Filters&nbsp;
          </Typography>
          <Typography marginY="0" color="light-grey">
            {activeFilters !== 0 && `(${activeFilters})`}
          </Typography>
        </Button>
        <Button
          width="75px"
          fontSize="body-small"
          height="19px"
          variant="tertiary"
          icon="trash"
          iconHeight="14px"
          iconWidth="13px"
          padding="0"
          disabled={isClearAllDisabled}
          text="Clear All"
          onClick={onClearAll}
          data-en="click"
          data-ec="button"
          data-el="filter"
          data-ea="clear-all"
          data-es=""
        />
      </Box>
      <ActiveFilters
        selectedFilterLabel={selectedFilterLabel}
        selectedSortOptionLabel={selectedSortOptionLabel}
        vrmQuery={purchasesUrlQueryParams.q}
        setPurchasesUrlParams={setPurchasesUrlParams}
        appliedCheckboxes={appliedCheckboxes}
        setAppliedCheckboxes={setAppliedCheckboxes}
        setAdditionalFilters={updateAdditionalFilters}
        additionalFilters={purchasesUrlQueryParams.additionalFilters}
        setAppliedCheckboxesSingleSelect={setAppliedCheckboxesSingleSelect}
      />
    </Box>
  );
};

export default FiltersMobile;
